import {
  TActivityLevel,
  TBodyType,
  THealthConcernsToAddress,
  TMotivationsForSwitch,
  IAgeViewState,
} from "@./state";
import { Theme } from "apps/website/maps/Theme.map";
import {
  NIBBLES_PRODUCT_TITLE_MAP,
} from "libs/api/customer/src/lib/types/products";
import { FreshMealPlanType } from "libs/api/customer/src/lib/types/checkout";

import {
  Allergen,
  CatFoodTypeBefore,
  DiscountCodeType,
  PricingPlan,
} from "@/primary-models";

export const defaultUser = {
  firstName: "",
  lastName: "",
  email: "",
  marketingConsent: true,
  shippingAddress: {},
  billingAddress: {},
  id: undefined,
};

export const defaultOngoingDeliveryFee = 2.99;
export const defaultTrialDeliveryFee = 0;
export const defaultDiscountCode = "FRESH20";
export const defaultProductType: FreshMealPlanType = "FRESH_TRIAL";

export const NIBBLES_CHICKEN_PRICE_BEFORE_DISCOUNT = 7.50;
export const NIBBLES_CHICKEN_PRICE_AFTER_DISCOUNT = 3.75;
export const NIBBLES_SALMON_PRICE_BEFORE_DISCOUNT = 9.5;
export const NIBBLES_SALMON_PRICE_AFTER_DISCOUNT = 4.75;
export const SPRINKLES_PRICE_BEFORE_DISCOUNT = 0;
export const SPRINKLES_PRICE_AFTER_DISCOUNT = 0;

export const defaultDelivery = {
  ongoingDeliveryFee: defaultOngoingDeliveryFee,
  trialDeliveryFee: defaultTrialDeliveryFee,
  deliveryDate: undefined,
};

export const defaultDiscount = {
  discountCode: defaultDiscountCode,
  type: DiscountCodeType.PERCENTAGE,
  value: "20",
};

export const defaultSubscription = {
  discount: defaultDiscount,
  productType: defaultProductType,
  trial: {
    planType: PricingPlan.TRIAL,
  },
  ongoing: {
    planType: PricingPlan.STANDARD,
  },
  nibblesUpsell: {
    added: false,
    discountedPrice: NIBBLES_CHICKEN_PRICE_AFTER_DISCOUNT,
    originalPrice: NIBBLES_CHICKEN_PRICE_BEFORE_DISCOUNT,
    type: NIBBLES_PRODUCT_TITLE_MAP.NIBBLES_CHICKEN,
    quantity: 0,
  },
  sprinklesUpsell: {
    added: false,
    discountedPrice: SPRINKLES_PRICE_AFTER_DISCOUNT,
    originalPrice: SPRINKLES_PRICE_BEFORE_DISCOUNT,
    type: "SPRINKLES" as const,
    quantity: 0,
  },
};

export const defaultViewState = {
  checkout: {
    // Delivery/Shipping Address Section
    showManualEntryButton: true,
    showAddressSelect: false,
    showShippingAddressFields: false,
    hasClickedFindAddress: false,
    isDeliveryComplete: false,
    hasCompletedDeliveryOnce: false,
    hasSelectedAddressFromLookup: false,
    showShippingAddressWarning: false,
    showBillingAddressFields: false,
    billingSameAsShipping: true,
    showProcessingModal: false,
    isPaymentFormValid: false,
    shouldTriggerPayment: false,
  },
  catParentDetails: {
    isTransitionVisible: false,
  },
  age: {},
};

export const defaultAgeViewState: IAgeViewState = {
  showBirthdaySection: false,
  showNeuteredSection: false,
};

export const motivationsForSwitch: Record<TMotivationsForSwitch, TMotivationsForSwitch> = {
  SUPPORTS_HEALTH_CONDITIONS: "SUPPORTS_HEALTH_CONDITIONS",
  HELPS_WITH_HUNGER: "HELPS_WITH_HUNGER",
  LOVES_THE_TASTE: "LOVES_THE_TASTE",
  WANTS_THE_BEST: "WANTS_THE_BEST",
};

export const healthConcernsToAddress: Record<THealthConcernsToAddress, THealthConcernsToAddress> = {
  DULL_COAT: "DULL_COAT",
  SMELLY_POOP: "SMELLY_POOP",
  HEALTHY_WEIGHT: "HEALTHY_WEIGHT",
  LOW_ENERGY: "LOW_ENERGY",
  SICK_OR_DIARRHOEA: "SICK_OR_DIARRHOEA",
  OTHER: "OTHER",
};

export const bodyTypes: Record<TBodyType, TBodyType> = {
  SKINNY: "SKINNY",
  JUST_RIGHT: "JUST_RIGHT",
  CHUBBY: "CHUBBY",
  OBESE: "OBESE",
};

export const activityLevels: Record<TActivityLevel, TActivityLevel> = {
  INACTIVE: "INACTIVE",
  ACTIVE: "ACTIVE",
  EXPLORER: "EXPLORER",
};

export const FOOD_TYPES: Record<CatFoodTypeBefore, CatFoodTypeBefore> = {
  DRY: "DRY",
  FRESH: "FRESH",
  WET: "WET",
  RAW: "RAW",
  HOME_COOKED: "HOME_COOKED",
};

export enum SignupFlowSlug {
  // Cat pages
  CAT_NAMES = "/sign-up/fresh",
  BASIC_INFORMATION = "/sign-up/fresh/basic-information",
  AGE = "/sign-up/fresh/age",
  EATING_HABITS = "/sign-up/fresh/eating-habits",
  MOTIVATION = "/sign-up/fresh/motivation",
  FITNESS = "/sign-up/fresh/fitness",
  WEIGHT = "/sign-up/fresh/weight",
  CURRENT_DIET = "/sign-up/fresh/current-diet",
  MEDICAL_CONDITIONS = "/sign-up/fresh/medical-conditions",
  ALLERGIES = "/sign-up/fresh/allergies",

  // User pages
  CAT_PARENT_DETAILS = "/sign-up/fresh/cat-parent-details",
  CHECKOUT = "/sign-up/fresh/checkout",
  PRICING = "/sign-up/fresh/pricing",

  // Pages with no validation
  RECIPES = "/sign-up/fresh/recipes",
  TREATS = "/sign-up/fresh/treats",
}

export interface FlowNavigationContext {
  inABTestGroup: (flag: string) => boolean;
  hasAllAllergies: (allergies: Allergen[]) => boolean;
}

export type PageRequirement = (context: FlowNavigationContext) => boolean;

export interface FlowPage {
  slug: SignupFlowSlug;
  accessRequirements: PageRequirement[];
  isTransitionScreen?: boolean;
}

export interface ISignupFlowPageMetadata {
  title?: string
  hideProgressSteps?: boolean
  hideFooter?: boolean
  headerBgColor?: Theme
  bgColor?: Theme
  isUserPage?: boolean
  isTransitionScreen?: boolean
  copyCatAnswers?: boolean
  hidePromoBanner?: boolean
  promoBannerAnimateOnEntry?: boolean
  /**
 * Access requirements are used to determine if a page should be displayed to the user.
 * Example A/B test requirement
 * accessRequirements: [
      (context: FlowNavigationContext) => context.inABTestGroup("new-basic-information-flow"),
    ],
 */
  accessRequirements?: PageRequirement[]
}

export const SIGNUP_FLOW_PAGES: Record<SignupFlowSlug, ISignupFlowPageMetadata> = {
  [SignupFlowSlug.CAT_NAMES]: {
    title: "catNames",
    bgColor: "light",
    promoBannerAnimateOnEntry: true,
  },
  [SignupFlowSlug.BASIC_INFORMATION]: {
    title: "basicInformation",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.AGE]: {
    title: "age",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.EATING_HABITS]: {
    title: "eatingHabits",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.MOTIVATION]: {
    title: "motivation",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.FITNESS]: {
    title: "fitness",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.WEIGHT]: {
    title: "weight",
  },
  [SignupFlowSlug.CURRENT_DIET]: {
    title: "currentDiet",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.MEDICAL_CONDITIONS]: {
    title: "medicalConditions",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.ALLERGIES]: {
    title: "allergies",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.CAT_PARENT_DETAILS]: {
    hideProgressSteps: true,
    bgColor: "light-grey",
    headerBgColor: "light-grey",
    isUserPage: true,
  },
  [SignupFlowSlug.RECIPES]: {
    title: "recipes",
    hideProgressSteps: true,
    hidePromoBanner: true,
  },
  [SignupFlowSlug.PRICING]: {
    title: "pricing",
    hideProgressSteps: true,
    isUserPage: true,
    bgColor: "light",
    hidePromoBanner: true,
  },
  [SignupFlowSlug.TREATS]: {
    hideProgressSteps: true,
    isUserPage: true,
    hidePromoBanner: true,
    accessRequirements: [
      (context: FlowNavigationContext) => !context.hasAllAllergies([ "CHICKEN", "SALMON" ]),
    ],
  },
  [SignupFlowSlug.CHECKOUT]: {
    title: "checkout",
    hideProgressSteps: true,
    isUserPage: true,
    bgColor: "light",
    hidePromoBanner: true,
  },
};

export const flowPages: FlowPage[] = Object.entries(SIGNUP_FLOW_PAGES).map(
  ([ slug, metadata ]) => ({
    slug: slug as SignupFlowSlug,
    accessRequirements: metadata.accessRequirements ?? [],
    isTransitionScreen: metadata.isTransitionScreen,
  }),
);
